import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { usePOS } from '@/contexts/POSContext';
import { useTheme } from '@/hooks/useTheme';
import { CURRENCIES, getCurrencySymbol } from '@/lib/bch';
import { cn } from '@/lib/utils';
import { FREE_PRODUCT_LIMIT } from '@/lib/pro';
import { PUSD } from '@/lib/stablecoin';
import { TaxRate, Coupon, upsertCoupon, deleteCoupon, loadCoupons, loadProducts, saveProducts, saveSettings, loadTransactions, POSSettings } from '@/lib/posStorage';
import { loadMerchantIdentity, ensureMerchantIdentity, clearMerchantIdentity, encodeGeohash, type MerchantIdentity } from '@/lib/merchantNostr';
import { useMerchantNostr } from '@/hooks/useMerchantNostr';

/**
 * Basic BCH address format validation.
 * Accepts:
 *   - CashAddr with prefix:    bitcoincash:q... / bitcoincash:p...
 *   - CashAddr without prefix: q... / p...  (42 chars)
 *   - Legacy Base58:           1... / 3...  (25–34 chars)
 */
function isValidBCHAddress(addr: string): boolean {
  const a = addr.trim();
  if (!a) return false;
  // CashAddr with prefix
  if (/^bitcoincash:[qp][a-z0-9]{41}$/i.test(a)) return true;
  // CashAddr without prefix (42 chars starting with q or p)
  if (/^[qp][a-z0-9]{41}$/i.test(a)) return true;
  // Legacy Base58Check
  if (/^[13][a-zA-Z0-9]{24,33}$/.test(a)) return true;
  return false;
}

function resizeLogoDataUrl(file: File, maxPx = 512): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = e.target!.result as string;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function CouponForm({ initial, currency, onSave, onCancel }: {
  initial?: Coupon;
  currency: string;
  onSave: (c: Coupon) => void;
  onCancel: () => void;
}) {
  const sym = getCurrencySymbol(currency);
  const [code, setCode] = useState(initial?.code ?? '');
  const [type, setType] = useState<'percent' | 'flat'>(initial?.type ?? 'percent');
  const [value, setValue] = useState(initial?.value?.toString() ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [active, setActive] = useState(initial?.active ?? true);

  const handleSave = () => {
    if (!code.trim()) return alert('Enter a coupon code.');
    const val = parseFloat(value);
    if (isNaN(val) || val <= 0) return alert('Enter a valid discount value.');
    onSave({
      id: initial?.id ?? crypto.randomUUID(),
      code: code.trim().toUpperCase(),
      type, value: val, description: description.trim() || undefined, active,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-white dark:bg-zinc-900 rounded-t-3xl p-5 pb-safe space-y-4">
        <div className="w-10 h-1 bg-zinc-200 dark:bg-zinc-700 rounded-full mx-auto" />
        <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-50">{initial ? 'Edit Coupon' : 'New Coupon Code'}</h3>

        <div>
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Code</label>
          <input type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="SUMMER10" maxLength={20}
            className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm font-mono text-zinc-800 dark:text-zinc-100 uppercase focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
          />
        </div>

        <div className="flex gap-2">
          {(['percent', 'flat'] as const).map((t) => (
            <button key={t} onClick={() => setType(t)}
              className={cn('flex-1 py-2.5 rounded-xl text-sm font-semibold border transition-all',
                type === t ? 'bg-[#0AC18E] border-[#0AC18E] text-white' : 'bg-zinc-50 dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-500'
              )}>
              {t === 'percent' ? '% Percent off' : `${sym} Flat amount off`}
            </button>
          ))}
        </div>

        <div>
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">
            Value ({type === 'percent' ? '%' : sym})
          </label>
          <input type="number" inputMode="decimal" min="0" step="0.01" value={value} onChange={(e) => setValue(e.target.value)}
            placeholder={type === 'percent' ? '10' : '5.00'}
            className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1.5">Description (optional)</label>
          <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Summer sale, Employee discount"
            className="w-full bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
          />
        </div>

        <div className="flex items-center justify-between bg-zinc-50 dark:bg-zinc-800 rounded-xl px-4 py-3 border border-zinc-200 dark:border-zinc-700">
          <p className="text-sm font-medium text-zinc-700 dark:text-zinc-200">Active</p>
          <button onClick={() => setActive((v) => !v)}
            className={cn('relative w-12 h-6 rounded-full transition-colors', active ? 'bg-[#0AC18E]' : 'bg-zinc-300 dark:bg-zinc-600')}>
            <span className={cn('absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform', active ? 'translate-x-6' : 'translate-x-0')} />
          </button>
        </div>

        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-4 rounded-2xl text-sm font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-500 border border-zinc-200 dark:border-zinc-700">Cancel</button>
          <button onClick={handleSave} className="flex-[2] py-4 rounded-2xl text-sm font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30">
            {initial ? 'Save Changes' : 'Create Coupon'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Install instructions ─────────────────────────────────────────────────────

type OS = 'ios' | 'android' | 'desktop' | 'unknown';

function detectOS(): OS {
  const ua = navigator.userAgent;
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  if (/android/i.test(ua)) return 'android';
  if (/Windows|Macintosh|Linux/.test(ua)) return 'desktop';
  return 'unknown';
}

// BeforeInstallPromptEvent is not in standard lib types
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function InstallInstructions() {
  const [os] = useState<OS>(detectOS);
  const [isInstalled, setIsInstalled] = useState(false);
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installing, setInstalling] = useState(false);
  const [expanded, setExpanded] = useState<'ios' | 'android' | 'desktop' | null>(null);

  // Capture the browser's native install prompt (Android / desktop Chrome)
  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Detect if already running as installed PWA
  useEffect(() => {
    const mq = window.matchMedia('(display-mode: standalone)');
    setIsInstalled(mq.matches || (navigator as unknown as { standalone?: boolean }).standalone === true);
    const handler = (e: MediaQueryListEvent) => setIsInstalled(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const handleNativeInstall = async () => {
    if (!installPrompt) return;
    setInstalling(true);
    await installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === 'accepted') setInstallPrompt(null);
    setInstalling(false);
  };

  if (isInstalled) {
    return (
      <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700">
        <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">Install on Device</p>
        <div className="flex items-center gap-3 bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 rounded-xl px-4 py-3 border border-[#0AC18E]/20">
          <span className="text-2xl shrink-0">✅</span>
          <div>
            <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">Already installed!</p>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
              BCH POS is running as an app on this device.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const Step = ({ n, children }: { n: number; children: React.ReactNode }) => (
    <div className="flex gap-3">
      <span className="w-6 h-6 rounded-full bg-[#0AC18E] text-white text-xs font-black flex items-center justify-center shrink-0 mt-0.5">
        {n}
      </span>
      <p className="text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{children}</p>
    </div>
  );

  const SectionToggle = ({ id, icon, label, sublabel }: { id: 'ios' | 'android' | 'desktop'; icon: string; label: string; sublabel: string }) => (
    <button
      onClick={() => setExpanded(expanded === id ? null : id)}
      className={cn(
        'w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 text-left transition-all',
        expanded === id
          ? 'bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 border-[#0AC18E]/40'
          : 'bg-zinc-50 dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700'
      )}
    >
      <span className="text-2xl shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">{label}</p>
        <p className="text-xs text-zinc-400">{sublabel}</p>
      </div>
      <span className={cn(
        'text-zinc-400 text-sm transition-transform shrink-0',
        expanded === id ? 'rotate-180' : ''
      )}>▾</span>
    </button>
  );

  return (
    <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700 space-y-4">
      {/* Header */}
      <div>
        <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-1">Install on Device</p>
        <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
          Add BCH POS to your home screen so it opens like a native app — full screen, no browser bar.
        </p>
      </div>

      {/* One-tap install button (Android / Chrome desktop) */}
      {installPrompt && (
        <button
          onClick={handleNativeInstall}
          disabled={installing}
          className="w-full py-4 rounded-2xl text-base font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-98 transition-all disabled:opacity-70"
        >
          {installing ? 'Installing…' : '📲 Install App Now'}
        </button>
      )}

      {/* Platform instructions */}
      <div className="space-y-2">
        {/* iOS */}
        <div>
          <SectionToggle id="ios" icon="🍎" label="iPhone or iPad" sublabel="Safari browser required" />
          {expanded === 'ios' && (
            <div className="mt-2 mx-1 bg-zinc-50 dark:bg-zinc-900 rounded-xl p-4 space-y-3 border border-zinc-200 dark:border-zinc-700">
              <Step n={1}>
                Open this page in <span className="font-bold text-zinc-800 dark:text-zinc-200">Safari</span>.{' '}
                <span className="text-zinc-400">(Chrome and Firefox can't install PWAs on iOS.)</span>
              </Step>
              <Step n={2}>
                Tap the <span className="font-bold text-zinc-800 dark:text-zinc-200">Share button</span>{' '}
                <span className="inline-flex items-center gap-0.5 bg-zinc-200 dark:bg-zinc-700 px-1.5 py-0.5 rounded text-xs font-mono text-zinc-700 dark:text-zinc-200">
                  □↑
                </span>{' '}
                at the bottom of the screen (or top-right on iPad).
              </Step>
              <Step n={3}>
                Scroll down in the share sheet and tap{' '}
                <span className="font-bold text-zinc-800 dark:text-zinc-200">"Add to Home Screen"</span>.
              </Step>
              <Step n={4}>
                Tap <span className="font-bold text-zinc-800 dark:text-zinc-200">"Add"</span> in the top-right corner.
              </Step>
              <div className="bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 rounded-xl px-3 py-2.5 border border-[#0AC18E]/20">
                <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  <span className="font-semibold text-[#0AC18E]">✓ Done!</span>{' '}
                  BCH POS now appears on your home screen. Tap its icon to open it full-screen like a native app.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Android */}
        <div>
          <SectionToggle id="android" icon="🤖" label="Android Phone or Tablet" sublabel="Chrome browser recommended" />
          {expanded === 'android' && (
            <div className="mt-2 mx-1 bg-zinc-50 dark:bg-zinc-900 rounded-xl p-4 space-y-3 border border-zinc-200 dark:border-zinc-700">
              {installPrompt ? (
                <div className="bg-[#0AC18E]/10 border border-[#0AC18E]/20 rounded-xl px-3 py-2.5">
                  <p className="text-sm font-semibold text-[#0AC18E]">
                    ✓ Use the "Install App Now" button above — one tap!
                  </p>
                </div>
              ) : (
                <>
                  <Step n={1}>
                    Open <span className="font-bold text-zinc-800 dark:text-zinc-200">bchpay.cash</span> in <span className="font-bold text-zinc-800 dark:text-zinc-200">Chrome</span>.
                  </Step>
                  <Step n={2}>
                    Tap the <span className="font-bold text-zinc-800 dark:text-zinc-200">three-dot menu ⋮</span> in the top-right corner.
                  </Step>
                  <Step n={3}>
                    Tap <span className="font-bold text-zinc-800 dark:text-zinc-200">"Add to Home screen"</span>. If you see <span className="font-bold text-zinc-800 dark:text-zinc-200">"Install app"</span> instead, tap that.
                  </Step>
                  <Step n={4}>
                    Tap <span className="font-bold text-zinc-800 dark:text-zinc-200">"Install"</span> to confirm.
                  </Step>
                </>
              )}
              <div className="bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 rounded-xl px-3 py-2.5 border border-[#0AC18E]/20">
                <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  <span className="font-semibold text-[#0AC18E]">✓ Done!</span>{' '}
                  BCHPAY.Cash opens full-screen from your home screen or app drawer.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Desktop Chrome */}
        <div>
          <SectionToggle id="desktop-chrome" icon="🖥️" label="Desktop — Chrome" sublabel="Windows, Mac or Linux" />
          {expanded === 'desktop-chrome' && (
            <div className="mt-2 mx-1 bg-zinc-50 dark:bg-zinc-900 rounded-xl p-4 space-y-3 border border-zinc-200 dark:border-zinc-700">
              {installPrompt ? (
                <div className="bg-[#0AC18E]/10 border border-[#0AC18E]/20 rounded-xl px-3 py-2.5">
                  <p className="text-sm font-semibold text-[#0AC18E]">
                    ✓ Use the "Install App Now" button above — one click!
                  </p>
                </div>
              ) : (
                <>
                  <Step n={1}>
                    Open <span className="font-bold text-zinc-800 dark:text-zinc-200">bchpay.cash</span> in <span className="font-bold text-zinc-800 dark:text-zinc-200">Chrome</span>.
                  </Step>
                  <Step n={2}>
                    Look for the <span className="font-bold text-zinc-800 dark:text-zinc-200">install icon ⊕</span> in the address bar (right side) and click it.
                  </Step>
                  <Step n={3}>
                    Click <span className="font-bold text-zinc-800 dark:text-zinc-200">"Install"</span> in the dialog.
                  </Step>
                  <Step n={4}>
                    No icon? Click the <span className="font-bold text-zinc-800 dark:text-zinc-200">three-dot menu ⋮</span> → <span className="font-bold text-zinc-800 dark:text-zinc-200">"Save and share"</span> → <span className="font-bold text-zinc-800 dark:text-zinc-200">"Install page as app…"</span>
                  </Step>
                </>
              )}
              <div className="bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 rounded-xl px-3 py-2.5 border border-[#0AC18E]/20">
                <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  <span className="font-semibold text-[#0AC18E]">✓ Done!</span>{' '}
                  BCHPAY.Cash opens in its own window from your taskbar or dock.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Desktop Edge */}
        <div>
          <SectionToggle id="desktop-edge" icon="🌐" label="Desktop — Edge" sublabel="Windows or Mac" />
          {expanded === 'desktop-edge' && (
            <div className="mt-2 mx-1 bg-zinc-50 dark:bg-zinc-900 rounded-xl p-4 space-y-3 border border-zinc-200 dark:border-zinc-700">
              <Step n={1}>
                Open <span className="font-bold text-zinc-800 dark:text-zinc-200">bchpay.cash</span> in <span className="font-bold text-zinc-800 dark:text-zinc-200">Microsoft Edge</span>.
              </Step>
              <Step n={2}>
                Click the <span className="font-bold text-zinc-800 dark:text-zinc-200">three-dot menu …</span> in the top-right corner.
              </Step>
              <Step n={3}>
                Click <span className="font-bold text-zinc-800 dark:text-zinc-200">"Apps"</span>, then <span className="font-bold text-zinc-800 dark:text-zinc-200">"Install this site as an app"</span>.
              </Step>
              <Step n={4}>
                Click <span className="font-bold text-zinc-800 dark:text-zinc-200">"Install"</span> to confirm.
              </Step>
              <div className="bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 rounded-xl px-3 py-2.5 border border-[#0AC18E]/20">
                <p className="text-xs text-zinc-600 dark:text-zinc-300 leading-relaxed">
                  <span className="font-semibold text-[#0AC18E]">✓ Done!</span>{' '}
                  BCHPAY.Cash opens in its own window from your taskbar or Start menu.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Note about data */}
      <div className="flex gap-2.5 bg-zinc-50 dark:bg-zinc-900 rounded-xl px-3 py-3 border border-zinc-200 dark:border-zinc-700">
        <span className="text-base shrink-0">💾</span>
        <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
          Your settings, products, and transaction history are stored on <span className="font-semibold text-zinc-700 dark:text-zinc-200">this device</span>.
          Reinstalling or clearing browser data will erase them — export a CSV backup from the History tab first.
        </p>
      </div>
    </div>
  );
}

// ─── Backup / Restore ─────────────────────────────────────────────────────────

interface BackupBundle {
  v: 2;
  exportedAt: number;
  appName: string;
  settings: POSSettings;
  products: ReturnType<typeof loadProducts>;
  coupons: Coupon[];
  transactions: ReturnType<typeof loadTransactions>;
}

function BackupRestore({ onImported }: { onImported: () => void }) {
  const { settings } = usePOS();
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<'ok' | 'error' | null>(null);
  const [importStats, setImportStats] = useState<{ transactions: number; products: number } | null>(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [pendingBundle, setPendingBundle] = useState<BackupBundle | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Compute new-to-merge count once when the bundle is set, not on every render
  const newToMergeCount = useMemo(() => {
    if (!pendingBundle) return 0;
    const existingIds = new Set(loadTransactions().map((t) => t.id));
    return pendingBundle.transactions.filter((t) => !existingIds.has(t.id)).length;
  }, [pendingBundle]);

  const txCount = loadTransactions().length;
  const productCount = loadProducts().length;

  const handleExport = () => {
    const bundle: BackupBundle = {
      v: 2,
      exportedAt: Date.now(),
      appName: 'BCHPAY.Cash',
      settings,
      products: loadProducts(),
      coupons: loadCoupons(),
      transactions: loadTransactions(),
    };
    const json = JSON.stringify(bundle);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const date = new Date().toISOString().slice(0, 10);
    const safeName = (settings.merchantName || 'bchpay').replace(/[^a-z0-9]/gi, '-').toLowerCase();
    a.download = `${safeName}-backup-${date}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const raw = JSON.parse(ev.target!.result as string);
        // Support both v1 (old setup-only) and v2 (full backup)
        if (!raw.settings || !raw.products) throw new Error('Invalid file');
        const bundle: BackupBundle = {
          v: 2,
          exportedAt: raw.exportedAt ?? 0,
          appName: raw.appName ?? 'BCHPAY.Cash',
          settings: raw.settings,
          products: raw.products,
          coupons: raw.coupons ?? [],
          transactions: raw.transactions ?? [],
        };
        setPendingBundle(bundle);
        setConfirmRestore(true);
      } catch {
        setImportResult('error');
        setTimeout(() => setImportResult(null), 4000);
      } finally {
        setImporting(false);
        e.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  const handleConfirmRestore = () => {
    if (!pendingBundle) return;

    saveSettings(pendingBundle.settings);
    saveProducts(pendingBundle.products);
    pendingBundle.coupons.forEach((c) => upsertCoupon(c));

    // Merge transactions — keep all unique ones by id, device wins on conflict
    const existing = loadTransactions();
    const existingIds = new Set(existing.map((t) => t.id));
    const backupOnly = pendingBundle.transactions.filter((t) => !existingIds.has(t.id));
    // Combine and sort newest first
    const merged = [...existing, ...backupOnly].sort((a, b) => b.timestamp - a.timestamp);
    localStorage.setItem('bch-pos:transactions', JSON.stringify(merged.slice(0, 500)));

    const newCount = backupOnly.length;
    setImportStats({
      transactions: newCount,
      products: pendingBundle.products.length,
    });
    setImportResult('ok');
    setConfirmRestore(false);
    setPendingBundle(null);
    setTimeout(() => {
      setImportResult(null);
      setImportStats(null);
      onImported();
    }, 3000);
  };

  const formatDate = (ts: number) => ts
    ? new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : 'Unknown date';

  return (
    <>
      <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-100 dark:border-zinc-700 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-100 dark:border-zinc-700">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-base">💾</span>
            <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">Backup &amp; Restore</p>
          </div>
          <p className="text-xs text-zinc-400 leading-relaxed">
            Your data lives on this device. If you clear your browser or switch devices, it's gone — unless you have a backup.
          </p>
        </div>

        <div className="p-5 space-y-3">
          {/* Current data summary */}
          <div className="flex gap-2">
            <div className="flex-1 bg-zinc-50 dark:bg-zinc-900 rounded-xl px-3 py-2.5 border border-zinc-200 dark:border-zinc-700 text-center">
              <p className="text-lg font-black text-zinc-900 dark:text-zinc-50">{txCount}</p>
              <p className="text-xs text-zinc-400">transactions</p>
            </div>
            <div className="flex-1 bg-zinc-50 dark:bg-zinc-900 rounded-xl px-3 py-2.5 border border-zinc-200 dark:border-zinc-700 text-center">
              <p className="text-lg font-black text-zinc-900 dark:text-zinc-50">{productCount}</p>
              <p className="text-xs text-zinc-400">products</p>
            </div>
          </div>

          {/* Export */}
          <button
            onClick={handleExport}
            className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-amber-500 hover:bg-amber-400 active:scale-98 transition-all text-left shadow-md shadow-amber-500/30 border border-amber-400"
          >
            <span className="text-2xl shrink-0">💾</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white">Download Full Backup</p>
              <p className="text-xs text-white/80">Settings, products, coupons &amp; all {txCount} transactions</p>
            </div>
            <span className="text-lg font-bold text-white shrink-0">↓</span>
          </button>

          {/* Import */}
          <button
            onClick={() => fileRef.current?.click()}
            disabled={importing}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 transition-all text-left',
              importResult === 'ok'
                ? 'bg-[#0AC18E]/10 border-[#0AC18E]'
                : importResult === 'error'
                ? 'bg-red-50 dark:bg-red-900/20 border-red-400'
                : 'bg-zinc-50 dark:bg-zinc-900 border-dashed border-zinc-300 dark:border-zinc-600 hover:border-[#0AC18E]/50'
            )}
          >
            <span className="text-2xl shrink-0">
              {importResult === 'ok' ? '✅' : importResult === 'error' ? '❌' : '📂'}
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100">
                {importResult === 'ok'
                  ? `Restored! +${importStats?.transactions} new transactions merged`
                  : importResult === 'error'
                  ? 'Invalid backup file'
                  : 'Restore from Backup'}
              </p>
              <p className="text-xs text-zinc-400">
                {importResult === 'ok'
                  ? 'App reloading with your restored data…'
                  : importResult === 'error'
                  ? 'Make sure it was exported from BCHPAY.Cash'
                  : 'Load a .json backup file'}
              </p>
            </div>
            {!importResult && <span className="text-xs font-bold text-zinc-400 shrink-0">Choose →</span>}
          </button>
          <input ref={fileRef} type="file" accept=".json,application/json" className="hidden" onChange={handleFileSelect} />

          <p className="text-xs text-zinc-400 text-center">
            💡 Back up after every busy day or before clearing your browser
          </p>
        </div>
      </div>

      {/* Confirm restore dialog */}
      {confirmRestore && pendingBundle && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-white dark:bg-zinc-900 rounded-t-3xl p-5 pb-safe space-y-4">
            <div className="w-10 h-1 bg-zinc-200 dark:bg-zinc-700 rounded-full mx-auto" />
            <div>
              <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-50">Restore this backup?</h3>
              <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                Settings and products will be <span className="font-semibold text-amber-500">replaced</span> with the backup. Transactions will be <span className="font-semibold text-[#0AC18E]">merged</span> — your existing sales are kept and any missing ones from the backup are added.
              </p>
            </div>

            {/* Backup summary */}
            <div className="bg-zinc-50 dark:bg-zinc-800 rounded-2xl p-4 border border-zinc-200 dark:border-zinc-700 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Store</span>
                <span className="font-semibold text-zinc-800 dark:text-zinc-100">{pendingBundle.settings.merchantName || '—'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Backed up</span>
                <span className="font-semibold text-zinc-800 dark:text-zinc-100">{formatDate(pendingBundle.exportedAt)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Transactions in backup</span>
                <span className="font-semibold text-[#0AC18E]">{pendingBundle.transactions.length}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">New to merge in</span>
                <span className="font-semibold text-[#0AC18E]">+{newToMergeCount}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-500">Products</span>
                <span className="font-semibold text-zinc-800 dark:text-zinc-100">{pendingBundle.products.length}</span>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setConfirmRestore(false); setPendingBundle(null); }}
                className="flex-1 py-4 rounded-2xl text-sm font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-500 border border-zinc-200 dark:border-zinc-700"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRestore}
                className="flex-[2] py-4 rounded-2xl text-sm font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-98 transition-all"
              >
                ✓ Restore Backup
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export function SettingsView() {
  const { settings, setSettings, updateSettings, isPro, setView, refreshCoupons } = usePOS();
  const { theme, setTheme } = useTheme();

  const [address, setAddress] = useState(settings.bchAddress);
  const [merchantName, setMerchantName] = useState(settings.merchantName);
  const [merchantTagline, setMerchantTagline] = useState(settings.merchantTagline ?? '');
  const [brandColor, setBrandColor] = useState(settings.brandColor ?? '#0AC18E');
  const [currency, setCurrency] = useState(settings.currency);
  const [logoDataUrl, setLogoDataUrl] = useState<string | undefined>(settings.logoDataUrl);
  const [logoLoading, setLogoLoading] = useState(false);
  const [taxRate, setTaxRate] = useState(settings.taxRate.toString());
  const [taxName, setTaxName] = useState(settings.taxName);
  const [taxRates, setTaxRates] = useState<TaxRate[]>(() => settings.taxRates ?? []);
  const [skipReceipt, setSkipReceipt] = useState(settings.skipReceipt ?? false);
  const [dailySummary, setDailySummary] = useState(settings.dailySummary ?? false);
  const [enableTipping, setEnableTipping] = useState(settings.enableTipping ?? true);
  const [acceptPUSD, setAcceptPUSD] = useState(settings.acceptPUSD ?? false);
  const [publishActivity, setPublishActivity] = useState(settings.publishActivity ?? false);
  const [merchantCategory, setMerchantCategory] = useState(settings.merchantCategory ?? '');
  const [merchantWebsite, setMerchantWebsite] = useState(settings.merchantWebsite ?? '');
  const [merchantGeohash, setMerchantGeohash] = useState(settings.merchantGeohash ?? '');
  const [merchantLocationName, setMerchantLocationName] = useState(settings.merchantLocationName ?? '');
  const [merchantIdentity, setMerchantIdentity] = useState<MerchantIdentity | null>(() => loadMerchantIdentity());
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState<string | null>(null);
  const [locationQuery, setLocationQuery] = useState('');
  const [locationResults, setLocationResults] = useState<Array<{ name: string; lat: number; lon: number }>>([]);
  const [locationSearching, setLocationSearching] = useState(false);
  const locationDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [showNsec, setShowNsec] = useState(false);
  const [nsecCopied, setNsecCopied] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishResult, setPublishResult] = useState<'ok' | 'error' | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteResult, setDeleteResult] = useState<'ok' | 'partial' | null>(null);
  const { publishProfile, publishHeartbeat, deleteListing } = useMerchantNostr();
  const [saved, setSaved] = useState(false);

  // Coupons state (managed separately from main save)
  const [coupons, setCouponsLocal] = useState<Coupon[]>(() => loadCoupons());
  const [editingCoupon, setEditingCoupon] = useState<Coupon | 'new' | null>(null);

  const sym = getCurrencySymbol(currency);
  const fileRef = useRef<HTMLInputElement>(null);
  const isDark = theme === 'dark';

  const handleLogoPick = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoLoading(true);
    try {
      const dataUrl = await resizeLogoDataUrl(file, 512);
      setLogoDataUrl(dataUrl);
    } catch {
      alert('Could not load image. Please try another file.');
    } finally {
      setLogoLoading(false);
      e.target.value = '';
    }
  }, []);

  const handleSave = () => {
    const parsedTaxRate = Math.max(0, Math.min(99, parseFloat(taxRate) || 0));
    setSettings({
      bchAddress: address.trim(),
      merchantName: merchantName.trim(),
      merchantTagline: merchantTagline.trim(),
      brandColor: brandColor || '#0AC18E',
      currency,
      logoDataUrl,
      taxRate: parsedTaxRate,
      taxName: taxName.trim() || 'Tax',
      taxRates,
      skipReceipt,
      dailySummary,
      enableTipping,
      acceptPUSD,
      publishActivity,
      merchantCategory: merchantCategory.trim() || undefined,
      merchantWebsite: merchantWebsite.trim() || undefined,
      merchantGeohash: merchantGeohash.trim() || undefined,
      merchantLocationName: merchantLocationName.trim() || undefined,
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  // Tax rate helpers
  const addTaxRate = () => {
    setTaxRates((prev) => [...prev, { id: crypto.randomUUID(), name: 'New Rate', rate: 0 }]);
  };
  const updateTaxRate = (id: string, field: 'name' | 'rate', val: string) => {
    setTaxRates((prev) => prev.map((r) =>
      r.id === id ? { ...r, [field]: field === 'rate' ? (parseFloat(val) || 0) : val } : r
    ));
  };
  const removeTaxRate = (id: string) => setTaxRates((prev) => prev.filter((r) => r.id !== id));

  // Coupon helpers
  const saveCouponEdit = (c: Coupon) => {
    upsertCoupon(c);
    setCouponsLocal(loadCoupons());
    refreshCoupons();
    setEditingCoupon(null);
  };
  const removeCouponEntry = (id: string) => {
    deleteCoupon(id);
    setCouponsLocal(loadCoupons());
    refreshCoupons();
  };

  return (
    <div className="flex flex-col gap-5 pb-6">
      <div className="text-center">
        <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">Settings</h2>
        <p className="text-sm text-zinc-400">Configure your POS terminal</p>
      </div>

      {/* Plan status banner */}
      {isPro ? (
        <div className="bg-gradient-to-r from-[#0AC18E] to-[#09AF81] rounded-2xl px-5 py-4 flex items-center gap-3 shadow-sm shadow-[#0AC18E]/20">
          <span className="text-2xl">✅</span>
          <div>
            <p className="text-white font-bold text-sm">Pro Plan — All features unlocked</p>
            <p className="text-white/70 text-xs">Thank you for your support!</p>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setView('upgrade')}
          className="bg-zinc-900 dark:bg-zinc-800 rounded-2xl px-5 py-4 flex items-center gap-3 border border-zinc-700 w-full text-left"
        >
          <span className="text-2xl">⭐</span>
          <div className="flex-1 min-w-0">
            <p className="text-white font-bold text-sm">Free Plan</p>
            <p className="text-zinc-400 text-xs">{FREE_PRODUCT_LIMIT} products · Upgrade for full access</p>
          </div>
          <span className="text-xs font-bold text-[#0AC18E] bg-[#0AC18E]/15 px-3 py-1.5 rounded-full border border-[#0AC18E]/30 shrink-0">
            Upgrade →
          </span>
        </button>
      )}

      {/* ── Business Logo ── */}
      {isPro ? (
        <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-4">
            Business Logo
          </p>
          <div className="flex items-center gap-5">
            <div className="relative shrink-0">
              <div className={cn(
                'w-20 h-20 rounded-2xl overflow-hidden flex items-center justify-center border-2 transition-colors',
                logoDataUrl
                  ? 'border-[#0AC18E]/40 bg-white dark:bg-zinc-700'
                  : 'border-dashed border-zinc-300 dark:border-zinc-600 bg-zinc-50 dark:bg-zinc-700'
              )}>
                {logoLoading ? (
                  <div className="w-6 h-6 rounded-full border-2 border-[#0AC18E] border-t-transparent animate-spin" />
                ) : logoDataUrl ? (
                  <img src={logoDataUrl} alt="Business logo" className="w-full h-full object-contain p-1" />
                ) : (
                  <span className="text-3xl">🏪</span>
                )}
              </div>
              {logoDataUrl && !logoLoading && (
                <button
                  onClick={() => {
                    setLogoDataUrl(undefined);
                    updateSettings({ logoDataUrl: undefined });
                  }}
                  className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center shadow-md hover:bg-red-600 transition-colors"
                  aria-label="Remove logo"
                >✕</button>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 mb-1">
                {logoDataUrl ? 'Logo uploaded' : 'Upload your logo'}
              </p>
              <p className="text-xs text-zinc-400 dark:text-zinc-500 mb-3 leading-relaxed">
                Shown in the header and checkout. PNG with transparent background works best.
              </p>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="px-4 py-2 rounded-xl bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200 text-sm font-semibold border border-zinc-200 dark:border-zinc-600 hover:bg-zinc-200 dark:hover:bg-zinc-600 active:scale-95 transition-all"
              >
                {logoDataUrl ? '🔄 Replace Logo' : '📷 Choose Image'}
              </button>
            </div>
          </div>
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleLogoPick} />
        </div>
      ) : (
        /* Locked logo for free users */
        <button
          onClick={() => setView('upgrade')}
          className="w-full bg-[#0AC18E]/5 dark:bg-[#0AC18E]/10 border border-[#0AC18E]/20 dark:border-[#0AC18E]/30 rounded-2xl p-5 flex items-center gap-4 text-left"
        >
          <div className="w-14 h-14 rounded-2xl bg-[#0AC18E]/10 dark:bg-[#0AC18E]/20 flex items-center justify-center shrink-0 text-3xl">
            🏪
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-zinc-800 dark:text-zinc-100 mb-0.5">Business Logo</p>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-2">Upload your logo to brand the checkout screen</p>
            <span className="text-xs font-bold text-white bg-[#0AC18E] px-3 py-1 rounded-full">
              Upgrade to Pro →
            </span>
          </div>
        </button>
      )}

      {/* ── BCH Address ── */}
      <div className={cn(
        'rounded-2xl border-2 overflow-hidden transition-colors',
        !address
          ? 'border-amber-400 bg-white dark:bg-zinc-800'
          : address && !isValidBCHAddress(address)
          ? 'border-red-400 bg-white dark:bg-zinc-800'
          : 'border-[#0AC18E]/30 bg-white dark:bg-zinc-800'
      )}>
        {/* Header band — prominent when empty */}
        <div className={cn(
          'px-5 py-3 flex items-center gap-3',
          !address
            ? 'bg-amber-400'
            : address && !isValidBCHAddress(address)
            ? 'bg-red-500'
            : 'bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 border-b border-[#0AC18E]/20'
        )}>
          <div className={cn(
            'w-8 h-8 rounded-xl flex items-center justify-center shrink-0 text-base',
            !address
              ? 'bg-black/10'
              : address && !isValidBCHAddress(address)
              ? 'bg-white/20'
              : 'bg-[#0AC18E]/20'
          )}>
            {address && isValidBCHAddress(address) ? '✓' : '₿'}
          </div>
          <div className="flex-1 min-w-0">
            <p className={cn(
              'text-sm font-bold leading-snug',
              !address
                ? 'text-amber-900'
                : address && !isValidBCHAddress(address)
                ? 'text-white'
                : 'text-[#0AC18E]'
            )}>
              {!address
                ? 'Step 1: Enter your BCH address'
                : address && !isValidBCHAddress(address)
                ? 'Invalid BCH address'
                : 'BCH Receiving Address'}
            </p>
            <p className={cn(
              'text-xs leading-snug mt-0.5',
              !address
                ? 'text-amber-800'
                : address && !isValidBCHAddress(address)
                ? 'text-white/80'
                : 'text-[#0AC18E]/80'
            )}>
              {!address
                ? 'All payments go directly to this wallet — required to start'
                : address && !isValidBCHAddress(address)
                ? 'Check for typos — accepts CashAddr or legacy format'
                : 'Payments go directly to this wallet'}
            </p>
          </div>
          {!address && (
            <span className="text-amber-900 text-xs font-bold bg-black/10 px-2 py-1 rounded-full shrink-0">
              Required
            </span>
          )}
        </div>

        {/* Input area */}
        <div className="p-4">
          <input
            type="text"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder="bitcoincash:qr7545..."
            className={cn(
              'w-full bg-zinc-50 dark:bg-zinc-900 border rounded-xl px-4 py-3 text-sm font-mono text-zinc-800 dark:text-zinc-200 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:border-transparent',
              address && !isValidBCHAddress(address)
                ? 'border-red-300 focus:ring-red-400'
                : 'border-zinc-200 dark:border-zinc-600 focus:ring-[#0AC18E]'
            )}
          />
          {!address && (
            <p className="text-xs text-zinc-400 mt-2 leading-relaxed">
              Open your BCH wallet app, tap <span className="font-semibold text-zinc-600 dark:text-zinc-300">Receive</span>, and paste the address here.
            </p>
          )}
        </div>
      </div>

      {/* ── Merchant Discovery (Nostr) ── */}
      <div className={cn(
        'rounded-2xl overflow-hidden border-2 transition-colors',
        publishActivity
          ? 'border-purple-400/50 dark:border-purple-500/40'
          : 'border-purple-300/40 dark:border-purple-600/30'
      )}>
        {/* Gradient header — eye-catching */}
        <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-purple-700 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0">
              <span className="text-2xl">📡</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-black text-sm">Get Discovered by BCH Customers on BCH Local</p>
              <p className="text-white/75 text-xs mt-0.5">
                List your business on BCH Local — the decentralized BCH merchant directory
              </p>
            </div>
            {publishActivity && (
              <span className="text-xs font-bold text-white bg-white/20 px-2.5 py-1 rounded-full shrink-0 border border-white/30">
                Live
              </span>
            )}
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-800 p-5 space-y-4">
          {/* Enable toggle — large and prominent */}
          <button
            onClick={() => {
              const next = !publishActivity;
              setPublishActivity(next);
              if (next && !merchantIdentity) {
                // Generate keypair on first enable
                const id = ensureMerchantIdentity();
                setMerchantIdentity(id);
              }
            }}
            className={cn(
              'w-full flex items-center gap-3 px-4 py-4 rounded-2xl border-2 text-left transition-all',
              publishActivity
                ? 'bg-purple-50 dark:bg-purple-950/30 border-purple-400 dark:border-purple-500/60'
                : 'bg-zinc-50 dark:bg-zinc-900 border-dashed border-zinc-300 dark:border-zinc-600 hover:border-purple-300 dark:hover:border-purple-700'
            )}
          >
            <div className={cn(
              'w-12 h-7 rounded-full transition-colors duration-200 shrink-0 relative',
              publishActivity ? 'bg-purple-500' : 'bg-zinc-300 dark:bg-zinc-600'
            )}>
              <span
                className={cn(
                  'absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform duration-200',
                  publishActivity ? 'translate-x-5' : 'translate-x-0'
                )}
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className={cn(
                'text-sm font-bold',
                publishActivity ? 'text-purple-700 dark:text-purple-300' : 'text-zinc-700 dark:text-zinc-200'
              )}>
                {publishActivity ? 'Publishing — you are discoverable on BCH Local!' : 'Enable Merchant Discovery on BCH Local'}
              </p>
              <p className="text-xs text-zinc-400 mt-0.5">
                {publishActivity
                  ? 'A daily heartbeat signals you actively accept BCH.'
                  : 'Tap to list your business on bchlocal.com.'}
              </p>
            </div>
          </button>

          {/* Nostr identity card — shown when enabled */}
          {publishActivity && merchantIdentity && (
            <div className="bg-purple-50 dark:bg-purple-950/20 rounded-xl px-4 py-3 border border-purple-200 dark:border-purple-800/40 space-y-3">
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-sm">🔑</span>
                  <p className="text-xs font-bold text-purple-700 dark:text-purple-300">Your Merchant Nostr Identity</p>
                </div>
                <p className="text-xs font-mono text-purple-600 dark:text-purple-400 break-all leading-relaxed select-all">
                  {merchantIdentity.npub}
                </p>
                <p className="text-[10px] text-purple-400 dark:text-purple-500 mt-1.5">
                  This is a separate anonymous key used only for merchant discovery. It's not linked to any personal Nostr account.
                </p>
              </div>

              {/* Private key reveal */}
              <div className="border-t border-purple-200 dark:border-purple-800/40 pt-3">
                {showNsec ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-[10px] font-bold text-red-500 dark:text-red-400 uppercase tracking-wider">Private Key</p>
                      <button
                        onClick={() => { setShowNsec(false); setNsecCopied(false); }}
                        className="text-[10px] font-semibold text-purple-400 hover:text-purple-600 dark:hover:text-purple-300 transition-colors"
                      >
                        Hide
                      </button>
                    </div>
                    <div className="bg-white dark:bg-zinc-900 rounded-lg px-3 py-2.5 border border-red-200 dark:border-red-800/40">
                      <p className="text-xs font-mono text-red-600 dark:text-red-400 break-all leading-relaxed select-all">
                        {merchantIdentity.nsec}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(merchantIdentity.nsec).then(() => {
                          setNsecCopied(true);
                          setTimeout(() => setNsecCopied(false), 2500);
                        }).catch(() => {
                          // Fallback: select the text for manual copy
                        });
                      }}
                      className={cn(
                        'w-full py-2.5 rounded-xl text-xs font-bold transition-all',
                        nsecCopied
                          ? 'bg-[#0AC18E]/10 text-[#0AC18E] border border-[#0AC18E]/30'
                          : 'bg-purple-100 dark:bg-purple-950/30 text-purple-600 dark:text-purple-300 border border-purple-200 dark:border-purple-800/40 hover:bg-purple-200 dark:hover:bg-purple-900/40'
                      )}
                    >
                      {nsecCopied ? '✓ Copied to clipboard' : '📋 Copy Private Key'}
                    </button>
                    <div className="flex gap-2 bg-amber-50 dark:bg-amber-950/20 rounded-lg px-3 py-2.5 border border-amber-200 dark:border-amber-800/40">
                      <span className="text-sm shrink-0">⚠️</span>
                      <p className="text-[11px] text-amber-700 dark:text-amber-300 leading-relaxed">
                        <span className="font-bold">Save this somewhere safe.</span> You'll need it to recover your merchant listing if you clear your browser data or switch devices.
                      </p>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowNsec(true)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-xs font-semibold text-purple-500 dark:text-purple-400 bg-purple-100/50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-800/40 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
                  >
                    <span className="text-sm">👁</span>
                    Show Private Key
                  </button>
                )}
              </div>
            </div>
          )}

          {publishActivity && (
            <div className="space-y-3 pt-1">
              {/* Category */}
              <div>
                <label className="block text-xs font-semibold text-purple-500 dark:text-purple-400 uppercase tracking-wider mb-1.5">Category</label>
                <select
                  value={merchantCategory}
                  onChange={(e) => setMerchantCategory(e.target.value)}
                  className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-600 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-purple-400"
                >
                  <option value="">Select a category...</option>
                  <option value="cafe">Cafe / Coffee Shop</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="bar">Bar / Pub</option>
                  <option value="retail">Retail Store</option>
                  <option value="grocery">Grocery / Market</option>
                  <option value="food-truck">Food Truck</option>
                  <option value="bakery">Bakery</option>
                  <option value="salon">Salon / Barber</option>
                  <option value="fitness">Gym / Fitness</option>
                  <option value="services">Professional Services</option>
                  <option value="tech">Tech / Electronics</option>
                  <option value="travel">Travel / Tourism</option>
                  <option value="other">Other</option>
                </select>
              </div>

              {/* Website */}
              <div>
                <label className="block text-xs font-semibold text-purple-500 dark:text-purple-400 uppercase tracking-wider mb-1.5">Website (optional)</label>
                <input
                  type="url"
                  value={merchantWebsite}
                  onChange={(e) => setMerchantWebsite(e.target.value)}
                  placeholder="https://myshop.com"
                  className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-600 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-200 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-purple-400"
                />
              </div>

              {/* Location (optional) */}
              <div>
                <label className="block text-xs font-semibold text-purple-500 dark:text-purple-400 uppercase tracking-wider mb-1.5">
                  Store Location (optional)
                </label>
                <p className="text-xs text-zinc-400 mb-3 leading-relaxed">
                  Search for your city or neighborhood so customers can find nearby BCH merchants. Only your approximate area is shared — never your exact address.
                </p>

                {merchantGeohash ? (
                  /* Location set — show friendly name + clear button */
                  <div className="flex items-center gap-3 bg-purple-50 dark:bg-purple-950/20 rounded-xl px-4 py-3 border border-purple-200 dark:border-purple-800/40">
                    <span className="text-xl shrink-0">📍</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-purple-700 dark:text-purple-300">
                        {merchantLocationName || 'Location set'}
                      </p>
                      <p className="text-[11px] text-purple-400 dark:text-purple-500 mt-0.5">
                        Approximate area only
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        setMerchantGeohash('');
                        setMerchantLocationName('');
                        setLocationQuery('');
                        setLocationResults([]);
                        setLocError(null);
                      }}
                      className="text-xs font-bold text-red-400 hover:text-red-500 bg-red-50 dark:bg-red-950/30 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-800/40 transition-colors shrink-0"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  /* No location — show search input + GPS fallback */
                  <div className="space-y-2">
                    <div className="relative">
                      <input
                        type="text"
                        value={locationQuery}
                        onChange={(e) => {
                          const q = e.target.value;
                          setLocationQuery(q);
                          setLocError(null);

                          // Debounce search
                          if (locationDebounceRef.current) clearTimeout(locationDebounceRef.current);
                          if (q.trim().length < 2) {
                            setLocationResults([]);
                            setLocationSearching(false);
                            return;
                          }
                          setLocationSearching(true);
                          locationDebounceRef.current = setTimeout(async () => {
                            try {
                              const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(q.trim())}&limit=5&addressdetails=0`;
                              const res = await fetch(url, {
                                headers: { 'Accept': 'application/json' },
                              });
                              if (!res.ok) throw new Error('Search failed');
                              const data = await res.json() as Array<{ display_name: string; lat: string; lon: string }>;
                              setLocationResults(data.map((r) => ({
                                name: r.display_name,
                                lat: parseFloat(r.lat),
                                lon: parseFloat(r.lon),
                              })));
                            } catch {
                              setLocationResults([]);
                              setLocError('Search failed. Check your internet connection.');
                            } finally {
                              setLocationSearching(false);
                            }
                          }, 400);
                        }}
                        placeholder="Search city or neighborhood..."
                        className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-600 rounded-xl pl-10 pr-4 py-3 text-sm text-zinc-800 dark:text-zinc-200 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-purple-400 focus:border-transparent"
                      />
                      <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 text-sm pointer-events-none">
                        {locationSearching ? (
                          <span className="inline-block w-4 h-4 rounded-full border-2 border-purple-400 border-t-transparent animate-spin" />
                        ) : '🔍'}
                      </span>
                    </div>

                    {/* Search results dropdown */}
                    {locationResults.length > 0 && (
                      <div className="bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 rounded-xl overflow-hidden shadow-lg">
                        {locationResults.map((result, i) => (
                          <button
                            key={i}
                            onClick={() => {
                              const hash = encodeGeohash(result.lat, result.lon, 5);
                              setMerchantGeohash(hash);
                              // Build a short readable label from the full name
                              const parts = result.name.split(',').map((s) => s.trim());
                              const shortName = parts.length > 2
                                ? `${parts[0]}, ${parts[1]}`
                                : result.name;
                              setMerchantLocationName(shortName);
                              setLocationQuery('');
                              setLocationResults([]);
                            }}
                            className={cn(
                              'w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-purple-50 dark:hover:bg-purple-950/20 transition-colors',
                              i > 0 && 'border-t border-zinc-100 dark:border-zinc-700/50'
                            )}
                          >
                            <span className="text-purple-400 text-sm shrink-0 mt-0.5">📍</span>
                            <p className="text-sm text-zinc-700 dark:text-zinc-200 leading-snug">{result.name}</p>
                          </button>
                        ))}
                      </div>
                    )}

                    {/* No results message */}
                    {locationQuery.trim().length >= 2 && !locationSearching && locationResults.length === 0 && !locError && (
                      <p className="text-xs text-zinc-400 text-center py-2">No results found. Try a different search.</p>
                    )}

                    {/* Divider + GPS fallback */}
                    <div className="flex items-center gap-3 pt-1">
                      <div className="flex-1 border-t border-zinc-200 dark:border-zinc-700" />
                      <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-semibold">or</span>
                      <div className="flex-1 border-t border-zinc-200 dark:border-zinc-700" />
                    </div>

                    <button
                      onClick={() => {
                        if (!('geolocation' in navigator)) {
                          setLocError('Location is not supported on this device.');
                          return;
                        }
                        setLocating(true);
                        setLocError(null);
                        navigator.geolocation.getCurrentPosition(
                          (pos) => {
                            const hash = encodeGeohash(pos.coords.latitude, pos.coords.longitude, 5);
                            setMerchantGeohash(hash);
                            setMerchantLocationName('Current location');
                            setLocationQuery('');
                            setLocationResults([]);
                            setLocating(false);
                          },
                          (err) => {
                            setLocating(false);
                            if (err.code === 1) {
                              setLocError('Location permission denied. Try searching for your city instead.');
                            } else if (err.code === 2) {
                              setLocError('Could not detect location. Try searching for your city instead.');
                            } else {
                              setLocError('Location request timed out. Try searching for your city instead.');
                            }
                          },
                          { enableHighAccuracy: false, timeout: 15000, maximumAge: 300000 }
                        );
                      }}
                      disabled={locating}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold text-zinc-500 dark:text-zinc-400 hover:text-purple-600 dark:hover:text-purple-400 bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 hover:border-purple-300 dark:hover:border-purple-700 transition-all"
                    >
                      {locating ? (
                        <>
                          <span className="w-3.5 h-3.5 rounded-full border-2 border-purple-400 border-t-transparent animate-spin" />
                          Detecting...
                        </>
                      ) : (
                        <>
                          <span className="text-sm">📡</span>
                          Use device GPS instead
                        </>
                      )}
                    </button>
                  </div>
                )}

                {locError && (
                  <p className="text-xs text-red-500 mt-2 font-medium">{locError}</p>
                )}
              </div>

              {/* Publish Now button */}
              <button
                onClick={async () => {
                  setPublishing(true);
                  setPublishResult(null);
                  try {
                    // Clear throttle so heartbeat fires
                    localStorage.removeItem('bch-pos:last-heartbeat');
                    await publishProfile({
                      name: merchantName.trim() || 'BCH Merchant',
                      description: merchantTagline.trim() || undefined,
                      category: merchantCategory.trim() || undefined,
                      website: merchantWebsite.trim() || undefined,
                      geohash: merchantGeohash.trim() || undefined,
                    });
                    await publishHeartbeat();
                    setPublishResult('ok');
                  } catch {
                    setPublishResult('error');
                  } finally {
                    setPublishing(false);
                    setTimeout(() => setPublishResult(null), 4000);
                  }
                }}
                disabled={publishing}
                className={cn(
                  'w-full py-3.5 rounded-xl text-sm font-bold transition-all',
                  publishResult === 'ok'
                    ? 'bg-[#0AC18E] text-white'
                    : publishResult === 'error'
                    ? 'bg-red-500 text-white'
                    : 'bg-purple-600 hover:bg-purple-500 text-white shadow-md shadow-purple-600/30 active:scale-98'
                )}
              >
                {publishing
                  ? '📡 Publishing to Nostr...'
                  : publishResult === 'ok'
                  ? '✓ Published! Check /merchants'
                  : publishResult === 'error'
                  ? '✕ Failed — check your relay connection'
                  : '📡 Publish Now'}
              </button>

              {/* Privacy note */}
              <div className="flex gap-2.5 bg-purple-50 dark:bg-purple-950/20 rounded-xl px-3 py-3 border border-purple-200 dark:border-purple-800/40">
                <span className="text-base shrink-0">🔒</span>
                <p className="text-xs text-purple-800 dark:text-purple-200 leading-relaxed">
                  <span className="font-bold">Privacy first:</span> Uses a separate anonymous keypair. Your BCH address, transaction history, and personal
                  identity are <span className="font-bold">never</span> published.
                </p>
              </div>

            </div>
          )}

          {/* Delete listing — always visible when identity exists, even if toggle is off */}
          {merchantIdentity && (
            <button
              onClick={() => setConfirmDelete(true)}
              className="w-full py-3 rounded-xl text-xs font-semibold text-red-400 hover:text-red-500 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800/30 hover:border-red-300 dark:hover:border-red-700 transition-colors"
            >
              Delete My Listing &amp; Remove Identity
            </button>
          )}

          {/* Delete result banner */}
          {deleteResult && (
            <div className={cn(
              'rounded-xl px-4 py-3 border',
              deleteResult === 'ok'
                ? 'bg-[#0AC18E]/10 border-[#0AC18E]/30'
                : 'bg-amber-50 dark:bg-amber-950/20 border-amber-300 dark:border-amber-700/40'
            )}>
              <p className={cn(
                'text-sm font-bold',
                deleteResult === 'ok' ? 'text-[#0AC18E]' : 'text-amber-600 dark:text-amber-400'
              )}>
                {deleteResult === 'ok' ? '✓ Listing deleted' : '⚠ Local data cleared, relay deletion may not have worked'}
              </p>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1 leading-relaxed">
                {deleteResult === 'ok'
                  ? 'Deletion request sent to relays and local keypair removed.'
                  : 'Your local identity was removed. Relays may still have the old listing for a while.'}
              </p>
              <a
                href="/merchants"
                className="inline-block mt-2 text-xs font-bold text-purple-500 hover:text-purple-600 underline"
              >
                Verify on /merchants →
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Delete confirmation dialog — outside the card so it renders as overlay */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-lg bg-white dark:bg-zinc-900 rounded-t-3xl p-5 pb-safe space-y-4">
            <div className="w-10 h-1 bg-zinc-200 dark:bg-zinc-700 rounded-full mx-auto" />
            <div className="text-center">
              <span className="text-4xl block mb-2">🗑️</span>
              <h3 className="text-base font-bold text-zinc-900 dark:text-zinc-50">Delete your merchant listing?</h3>
              <p className="text-xs text-zinc-400 mt-2 leading-relaxed max-w-sm mx-auto">
                This will publish a deletion request to Nostr relays and remove your local merchant keypair.
                Your listing will disappear from the merchant directory. You can re-enable discovery later with a new identity.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setConfirmDelete(false)}
                className="flex-1 py-4 rounded-2xl text-sm font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-500 border border-zinc-200 dark:border-zinc-700"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setDeleting(true);
                  let relaySuccess = false;
                  try {
                    await deleteListing();
                    relaySuccess = true;
                  } catch {
                    // Continue with local cleanup even if relay delete fails
                  }
                  // Clear local state
                  clearMerchantIdentity();
                  setMerchantIdentity(null);
                  setPublishActivity(false);
                  setMerchantCategory('');
                  setMerchantWebsite('');
                  setMerchantGeohash('');
                  setMerchantLocationName('');
                  setConfirmDelete(false);
                  setDeleting(false);
                  // Also persist the toggle off
                  updateSettings({ publishActivity: false });
                  setDeleteResult(relaySuccess ? 'ok' : 'partial');
                  setTimeout(() => setDeleteResult(null), 10000);
                }}
                disabled={deleting}
                className="flex-[2] py-4 rounded-2xl text-sm font-bold bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/30 active:scale-98 transition-all disabled:opacity-70"
              >
                {deleting ? 'Deleting...' : '🗑️ Delete Listing'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── BCH Nostr ── */}
      <a
        href="https://bchnostr.com"
        target="_blank"
        rel="noopener noreferrer"
        className="block rounded-2xl overflow-hidden border-2 border-[#0AC18E]/40 hover:border-[#0AC18E]/70 transition-all active:scale-[0.98]"
      >
        <div className="bg-gradient-to-r from-[#0AC18E] to-[#08a87b] px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0">
              <span className="text-2xl">💬</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-black text-sm">Join the BCH Nostr Community</p>
              <p className="text-white/75 text-xs mt-0.5">The social hub for Bitcoin Cash merchants &amp; customers</p>
            </div>
            <span className="text-white/80 text-lg font-bold shrink-0">→</span>
          </div>
        </div>
        <div className="bg-[#0AC18E]/5 dark:bg-[#0AC18E]/10 px-5 py-3">
          <p className="text-xs text-zinc-500 dark:text-zinc-400 leading-relaxed">
            Connect with other BCH businesses, share your store, find customers, and stay up to date with the Bitcoin Cash community — all on a decentralized, censorship-free platform.
          </p>
          <p className="text-xs font-bold text-[#0AC18E] mt-1.5">bchnostr.com →</p>
        </div>
      </a>

      {/* ── Merchant Name ── */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700">
        <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">
          Merchant / Store Name
        </label>
        <input
          type="text"
          value={merchantName}
          onChange={(e) => setMerchantName(e.target.value)}
          placeholder="My Store"
          className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-600 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-200 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E] focus:border-transparent"
        />
        <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2 mt-4">
          Tagline <span className="font-normal normal-case text-zinc-300">(optional)</span>
        </label>
        <input
          type="text"
          value={merchantTagline}
          onChange={(e) => setMerchantTagline(e.target.value)}
          placeholder="e.g. Fresh food, fast service"
          maxLength={60}
          className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-600 rounded-xl px-4 py-3 text-sm text-zinc-800 dark:text-zinc-200 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E] focus:border-transparent"
        />
      </div>

      {/* ── Brand Color ── */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700">
        <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">
          Brand Color
        </label>
        <div className="flex items-center gap-4">
          <div className="relative shrink-0">
            <input
              type="color"
              value={brandColor}
              onChange={(e) => setBrandColor(e.target.value)}
              className="w-14 h-14 rounded-2xl border-2 border-zinc-200 dark:border-zinc-600 cursor-pointer bg-transparent p-1"
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 mb-1">
              Header &amp; accent color
            </p>
            <p className="text-xs text-zinc-400 mb-2">
              Used on checkout, receipt and success screens.
            </p>
            <div className="flex gap-2 flex-wrap">
              {['#0AC18E','#2563EB','#DC2626','#7C3AED','#D97706','#0891B2','#000000'].map((c) => (
                <button
                  key={c}
                  onClick={() => setBrandColor(c)}
                  style={{ backgroundColor: c }}
                  className={cn(
                    'w-7 h-7 rounded-lg border-2 transition-all active:scale-90',
                    brandColor === c ? 'border-zinc-900 dark:border-white scale-110' : 'border-transparent'
                  )}
                  aria-label={c}
                />
              ))}
            </div>
          </div>
        </div>
        {/* Live preview */}
        <div className="mt-4 rounded-xl overflow-hidden border border-zinc-200 dark:border-zinc-700">
          <div className="px-4 py-3 flex items-center gap-2" style={{ backgroundColor: brandColor }}>
            {logoDataUrl
              ? <img src={logoDataUrl} alt="preview" className="h-6 max-w-[80px] object-contain" />
              : <span className="text-white font-black text-xs">BCH</span>
            }
            <span className="text-white font-bold text-sm truncate">{merchantName || 'My Store'}</span>
          </div>
          <div className="bg-white dark:bg-zinc-900 px-4 py-2 flex items-center justify-between">
            <span className="text-xs text-zinc-400">{merchantTagline || 'Preview'}</span>
            <span className="text-xs font-bold" style={{ color: brandColor }}>✓ Paid</span>
          </div>
        </div>
      </div>

      {/* ── Currency ── */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700">
        <label className="block text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">
          Currency
        </label>
        <div className="grid grid-cols-2 gap-2">
          {CURRENCIES.map((c) => (
            <button
              key={c.code}
              onClick={() => setCurrency(c.code)}
              className={cn(
                'flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-medium border transition-all',
                currency === c.code
                  ? 'bg-[#0AC18E] text-white border-[#0AC18E] shadow-sm'
                  : 'bg-zinc-50 dark:bg-zinc-900 text-zinc-600 dark:text-zinc-300 border-zinc-200 dark:border-zinc-600 hover:border-[#0AC18E]/50'
              )}
            >
              <span className="text-base">{c.symbol}</span>
              <span>{c.code}</span>
              <span className="text-xs opacity-60 truncate">{c.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ── Tax / VAT ── */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700">
        <div className="flex items-center gap-2 mb-4">
          <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Tax / VAT</p>
          <span className="text-xs text-zinc-400">— leave 0 to disable</span>
        </div>
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="block text-xs font-medium text-zinc-400 mb-1.5">Label</label>
            <input
              type="text"
              value={taxName}
              onChange={(e) => setTaxName(e.target.value)}
              placeholder="Tax / VAT / GST"
              className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-600 rounded-xl px-3 py-3 text-sm text-zinc-800 dark:text-zinc-200 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
            />
          </div>
          <div className="w-28">
            <label className="block text-xs font-medium text-zinc-400 mb-1.5">Rate (%)</label>
            <div className="relative">
              <input
                type="number"
                inputMode="decimal"
                min="0"
                max="99"
                step="0.1"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                placeholder="0"
                className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-600 rounded-xl px-3 py-3 pr-7 text-sm text-zinc-800 dark:text-zinc-200 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">%</span>
            </div>
          </div>
        </div>
        {parseFloat(taxRate) > 0 && (
          <p className="text-xs text-[#0AC18E] mt-2.5 font-medium">
            {taxName || 'Tax'} at {taxRate}% will be added to every sale and shown on receipts.
          </p>
        )}
      </div>

      {/* ── Additional Tax Rates ── */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Product Tax Rates</p>
            <p className="text-xs text-zinc-400 mt-0.5">Assign different rates to products (e.g. Food, Alcohol)</p>
          </div>
          <button
            onClick={addTaxRate}
            className="text-xs font-bold text-[#0AC18E] bg-[#0AC18E]/10 px-3 py-1.5 rounded-lg hover:bg-[#0AC18E]/20 transition-colors"
          >
            + Add Rate
          </button>
        </div>
        {taxRates.length === 0 ? (
          <p className="text-xs text-zinc-400 text-center py-3">
            No extra rates yet. Using default rate for all products.
          </p>
        ) : (
          <div className="space-y-2">
            {taxRates.map((r) => (
              <div key={r.id} className="flex gap-2 items-center">
                <input
                  type="text"
                  value={r.name}
                  onChange={(e) => updateTaxRate(r.id, 'name', e.target.value)}
                  placeholder="Rate name"
                  className="flex-1 bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl px-3 py-2.5 text-sm text-zinc-800 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
                />
                <div className="relative w-24">
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    max="99"
                    step="0.1"
                    value={r.rate}
                    onChange={(e) => updateTaxRate(r.id, 'rate', e.target.value)}
                    className="w-full bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-xl px-3 py-2.5 pr-6 text-sm text-zinc-800 dark:text-zinc-200 focus:outline-none focus:ring-2 focus:ring-[#0AC18E]"
                  />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">%</span>
                </div>
                <button
                  onClick={() => removeTaxRate(r.id)}
                  className="w-9 h-9 rounded-xl bg-red-50 dark:bg-red-900/20 text-red-400 flex items-center justify-center hover:bg-red-100 transition-colors shrink-0"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-zinc-400 mt-3">
          After saving, assign rates to products in the Menu tab.
        </p>
      </div>

      {/* ── Coupon Codes ── */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Coupon Codes</p>
            <p className="text-xs text-zinc-400 mt-0.5">Create codes for promotions and discounts</p>
          </div>
          <button
            onClick={() => setEditingCoupon('new')}
            className="text-xs font-bold text-[#0AC18E] bg-[#0AC18E]/10 px-3 py-1.5 rounded-lg hover:bg-[#0AC18E]/20 transition-colors"
          >
            + Add Code
          </button>
        </div>

        {coupons.length === 0 ? (
          <p className="text-xs text-zinc-400 text-center py-3">No coupon codes yet.</p>
        ) : (
          <div className="space-y-2">
            {coupons.map((c) => (
              <div key={c.id} className="flex items-center gap-2 bg-zinc-50 dark:bg-zinc-900 rounded-xl px-3 py-2.5 border border-zinc-200 dark:border-zinc-700">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold font-mono text-zinc-800 dark:text-zinc-100">{c.code}</span>
                    {!c.active && <span className="text-xs text-zinc-400 bg-zinc-200 dark:bg-zinc-700 px-1.5 py-0.5 rounded-full">Inactive</span>}
                  </div>
                  <p className="text-xs text-zinc-400">
                    {c.type === 'percent' ? `${c.value}% off` : `${sym}${c.value} off`}
                    {c.description ? ` · ${c.description}` : ''}
                  </p>
                </div>
                <button onClick={() => setEditingCoupon(c)} className="text-xs text-zinc-400 hover:text-zinc-600 px-2 py-1">Edit</button>
                <button onClick={() => removeCouponEntry(c.id)} className="text-xs text-red-400 hover:text-red-600 px-2 py-1">Delete</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Coupon edit modal */}
      {editingCoupon !== null && (
        <CouponForm
          initial={editingCoupon === 'new' ? undefined : editingCoupon}
          currency={currency}
          onSave={saveCouponEdit}
          onCancel={() => setEditingCoupon(null)}
        />
      )}

      {/* ── Dark Mode ── */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">Dark Mode</p>
            <p className="text-xs text-zinc-400 mt-0.5">Switch between light and dark theme</p>
          </div>
          <button
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
            className={cn(
              'relative w-14 h-7 rounded-full transition-colors duration-200',
              isDark ? 'bg-[#0AC18E]' : 'bg-zinc-200 dark:bg-zinc-600'
            )}
            aria-label="Toggle dark mode"
          >
            <span
              className={cn(
                'absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform duration-200',
                isDark ? 'translate-x-7' : 'translate-x-0'
              )}
            />
          </button>
        </div>
      </div>

      {/* ── Skip Receipt ── */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0 pr-4">
            <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">Skip Receipt Screen</p>
            <p className="text-xs text-zinc-400 mt-0.5">
              Reset immediately after payment — no QR or summary shown. Faster for busy counters.
            </p>
          </div>
          <button
            onClick={() => setSkipReceipt((v) => !v)}
            className={cn(
              'relative w-14 h-7 rounded-full transition-colors duration-200 shrink-0',
              skipReceipt ? 'bg-[#0AC18E]' : 'bg-zinc-200 dark:bg-zinc-600'
            )}
            aria-label="Toggle skip receipt"
          >
            <span
              className={cn(
                'absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform duration-200',
                skipReceipt ? 'translate-x-7' : 'translate-x-0'
              )}
            />
          </button>
        </div>
      </div>

      {/* ── Tipping ── */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0 pr-4">
            <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">Enable Tipping</p>
            <p className="text-xs text-zinc-400 mt-0.5">
              Show a tip screen before payment. Great for restaurants, cafés, and services — turn off for retail.
            </p>
          </div>
          <button
            onClick={() => setEnableTipping((v) => !v)}
            className={cn(
              'relative w-14 h-7 rounded-full transition-colors duration-200 shrink-0',
              enableTipping ? 'bg-[#0AC18E]' : 'bg-zinc-200 dark:bg-zinc-600'
            )}
            aria-label="Toggle tipping"
          >
            <span
              className={cn(
                'absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform duration-200',
                enableTipping ? 'translate-x-7' : 'translate-x-0'
              )}
            />
          </button>
        </div>
        {!enableTipping && (
          <p className="text-xs text-[#0AC18E] mt-3 font-medium">
            💡 Tipping is off — customers go straight to payment after any discounts.
          </p>
        )}
        {enableTipping && (
          <p className="text-xs text-zinc-400 mt-3">
            💡 Studies show tip prompts increase average order value by 15–25% in service businesses.
          </p>
        )}
      </div>

      {/* ── Accept PUSD Stablecoin ── */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0 pr-4">
            <p className="flex items-center gap-2 text-sm font-semibold text-zinc-800 dark:text-zinc-200">
              {PUSD.iconUrl && <img src={PUSD.iconUrl} alt="" className="h-5 w-5 rounded-full shrink-0" />}
              Accept PUSD Stablecoin
              <span className="text-[10px] font-bold uppercase tracking-wide text-[#0AC18E] bg-[#0AC18E]/10 px-1.5 py-0.5 rounded-full align-middle">Beta</span>
            </p>
            <p className="text-xs text-zinc-400 mt-0.5">
              Let customers pay in ParyonUSD (PUSD) — a $1-pegged stablecoin on Bitcoin Cash. A BCH/PUSD toggle appears on the payment screen.
            </p>
          </div>
          <button
            onClick={() => setAcceptPUSD((v) => !v)}
            className={cn(
              'relative w-14 h-7 rounded-full transition-colors duration-200 shrink-0',
              acceptPUSD ? 'bg-[#0AC18E]' : 'bg-zinc-200 dark:bg-zinc-600'
            )}
            aria-label="Toggle PUSD acceptance"
          >
            <span
              className={cn(
                'absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform duration-200',
                acceptPUSD ? 'translate-x-7' : 'translate-x-0'
              )}
            />
          </button>
        </div>
        {acceptPUSD && (
          <div className="mt-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 rounded-xl px-3 py-2.5">
            <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
              ⚠️ Your wallet must support <span className="font-semibold">CashTokens</span> (e.g. Cashonize, Paytaca) to see and spend received PUSD. Payments go to the token-aware form of your BCH address automatically.
            </p>
          </div>
        )}
      </div>

      {/* ── Daily Summary Notification ── */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl p-5 border border-zinc-100 dark:border-zinc-700">
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0 pr-4">
            <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-200">Daily Sales Summary</p>
            <p className="text-xs text-zinc-400 mt-0.5">
              Get a notification each morning with yesterday's sales total and transaction count.
            </p>
          </div>
          <button
            onClick={() => {
              const next = !dailySummary;
              setDailySummary(next);
              // If enabling, request notification permission right away
              if (next && 'Notification' in window && Notification.permission === 'default') {
                Notification.requestPermission();
              }
            }}
            className={cn(
              'relative w-14 h-7 rounded-full transition-colors duration-200 shrink-0',
              dailySummary ? 'bg-[#0AC18E]' : 'bg-zinc-200 dark:bg-zinc-600'
            )}
            aria-label="Toggle daily summary"
          >
            <span
              className={cn(
                'absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow transition-transform duration-200',
                dailySummary ? 'translate-x-7' : 'translate-x-0'
              )}
            />
          </button>
        </div>
        {dailySummary && 'Notification' in window && Notification.permission === 'denied' && (
          <p className="text-xs text-amber-500 mt-3">
            ⚠️ Notifications are blocked in your browser. Enable them in your browser settings to receive summaries.
          </p>
        )}
      </div>

      {/* ── Install on Device ── */}
      <InstallInstructions />

      {/* ── Save ── */}
      {address && !isValidBCHAddress(address) && (
        <p className="text-sm text-red-500 text-center font-medium -mb-2">
          Fix the BCH address above before saving.
        </p>
      )}
      <button
        onClick={handleSave}
        disabled={!!(address && !isValidBCHAddress(address))}
        className={cn(
          'w-full py-4 rounded-2xl text-base font-bold transition-all duration-200',
          saved
            ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-500/30'
            : address && !isValidBCHAddress(address)
            ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-400 cursor-not-allowed'
            : 'bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-98'
        )}
      >
        {saved ? '✓ Saved!' : 'Save Settings'}
      </button>

      {/* ── Backup & Restore ── */}
      <BackupRestore onImported={() => window.location.reload()} />

      {/* ── About ── */}
      <div className="bg-white dark:bg-zinc-800 rounded-2xl border border-zinc-100 dark:border-zinc-700 overflow-hidden">
        {/* Header */}
        <div className="bg-[#0AC18E] px-5 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
            <span className="text-white font-black text-sm">BCH</span>
          </div>
          <div>
            <p className="text-white font-black text-base">BCH Point of Sale</p>
            <p className="text-white/75 text-xs">Accept Bitcoin Cash payments anywhere</p>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4">
          {/* No middleman */}
          <div className="flex gap-3">
            <span className="text-xl shrink-0">🔒</span>
            <div>
              <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                Payments go directly to you
              </p>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 leading-relaxed">
                There is no middleman. When a customer pays, BCH goes straight
                to the address you set in Settings — not through any server or
                third party. You can verify this by checking the destination
                address shown on every payment screen.
              </p>
            </div>
          </div>

          {/* Open source */}
          <div className="flex gap-3">
            <span className="text-xl shrink-0">📖</span>
            <div>
              <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                Open source &amp; auditable
              </p>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 leading-relaxed">
                This app is open source. Any developer can read the code and
                confirm it does exactly what it says. No hidden fees, no
                routing tricks, no data collection.
              </p>
            </div>
          </div>

          {/* No account */}
          <div className="flex gap-3">
            <span className="text-xl shrink-0">🙅</span>
            <div>
              <p className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                No account required
              </p>
              <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 leading-relaxed">
                Your settings, products, and transaction history are stored
                locally on this device. Nothing is sent to a server.
              </p>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-zinc-100 dark:border-zinc-700 pt-4 space-y-2">
            <a
              href="/brochure"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between py-2.5 px-3 rounded-xl bg-[#0AC18E]/10 dark:bg-[#0AC18E]/15 border border-[#0AC18E]/30 hover:border-[#0AC18E]/60 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className="text-base">📄</span>
                <span className="text-sm font-semibold text-[#0AC18E]">
                  Print / share a brochure
                </span>
              </div>
              <span className="text-[#0AC18E]/60 text-sm">→</span>
            </a>
            <a
              href="https://bitcoincash.org"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between py-2.5 px-3 rounded-xl bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 hover:border-[#0AC18E]/40 transition-colors"
            >
              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
                Learn about Bitcoin Cash
              </span>
              <span className="text-zinc-400 text-sm">→</span>
            </a>
            <a
              href="https://bchexplorer.cash"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between py-2.5 px-3 rounded-xl bg-zinc-50 dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 hover:border-[#0AC18E]/40 transition-colors"
            >
              <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">
                Verify transactions on BCH Explorer
              </span>
              <span className="text-zinc-400 text-sm">→</span>
            </a>
          </div>

          <p className="text-xs text-center text-zinc-400 pt-1">
            Vibed with{' '}
            <a
              href="https://shakespeare.diy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0AC18E] hover:underline"
            >
              Shakespeare
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
